/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 *
 * Documentation & Infrastructure — Section 2.6
 * Visible to SuperAdmin and TechSupport roles.
 *
 * Content lives in:
 *   src/data/docs/          ← all text, tables, and list data
 *   src/components/docs/    ← tab components and shared primitives
 */

import { useState } from 'react';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import { Construction, Architecture, AccountTree, Api, PeopleAlt, Rocket, Code, FactCheck } from '@mui/icons-material';

import InfrastructureTab from '../components/docs/InfrastructureTab';
import CicdTab           from '../components/docs/CicdTab';
import ApiTab            from '../components/docs/ApiTab';
import UserGuidesTab     from '../components/docs/UserGuidesTab';
import DeploymentTab     from '../components/docs/DeploymentTab';
import TechnicalTab      from '../components/docs/TechnicalTab';
import TestingTab        from '../components/docs/TestingTab';

const TABS = [
  { label: 'Infrastructure', icon: <Architecture fontSize="small" />, component: <InfrastructureTab /> },
  { label: 'CI/CD Pipeline', icon: <AccountTree fontSize="small" />,  component: <CicdTab /> },
  { label: 'API Reference',  icon: <Api fontSize="small" />,          component: <ApiTab /> },
  { label: 'User Guides',    icon: <PeopleAlt fontSize="small" />,    component: <UserGuidesTab /> },
  { label: 'Deployment',     icon: <Rocket fontSize="small" />,       component: <DeploymentTab /> },
  { label: 'Technical Docs', icon: <Code fontSize="small" />,         component: <TechnicalTab /> },
  { label: 'Test Checklist', icon: <FactCheck fontSize="small" />,    component: <TestingTab /> },
];

export default function Documentation() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Documentation</Typography>
        <Typography variant="body2" color="text.secondary">
          Infrastructure, CI/CD, API reference, user guides, deployment and troubleshooting.
          Sections marked <Construction sx={{ fontSize: 14, verticalAlign: 'middle', color: 'warning.main' }} /> are pending — developers should complete them before production launch.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        {TABS.map((t, i) => (
          <Tab key={t.label} label={t.label} icon={t.icon} iconPosition="start"
            id={`doc-tab-${i}`} sx={{ minHeight: 48, textTransform: 'none' }} />
        ))}
      </Tabs>

      {TABS.map((t, i) => (
        <Box key={t.label} role="tabpanel" hidden={tab !== i}>
          {tab === i && t.component}
        </Box>
      ))}
    </Box>
  );
}
